import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, KeyRound, RefreshCw, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import type { BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { useRolloutPreview, useStartRollout } from '@/hooks/useRollout';
import type { RolloutPreviewResponse } from '@/hooks/useRollout';
import {
  useEntkoppelnZugang,
  useZugangsmigration,
  zugangsmigrationKeys,
} from '@/hooks/useZugangsmigration';
import type {
  AuthMode,
  ZugangsmigrationFilter,
  ZugangsmigrationSystem,
} from '@/hooks/useZugangsmigration';
import { ApiError } from '@/lib/api';

/**
 * /systems/zugangsmigration - zweiter SSH-Weg (Yubikey) neben dem Tresor.
 * Der Schlüssel ersetzt das Passwort nicht; beide Wege bleiben sichtbar.
 */

type RolloutKind = 'ausrollen' | 'pruefen';

const AUTH_MODE_OPTIONS: { value: AuthMode | ''; label: string }[] = [
  { value: '', label: 'Alle' },
  { value: 'passwort', label: 'Nur Passwort' },
  { value: 'passwort+key', label: 'Passwort + Key' },
  { value: 'key', label: 'Nur Key' },
  { value: 'unbekannt', label: 'Kein Weg bekannt' },
  { value: 'manuell', label: 'Handarbeit' },
];

export const Route = createFileRoute('/systems/zugangsmigration')({
  component: ZugangsmigrationPage,
});

function ZugangsmigrationPage(): JSX.Element {
  const qc = useQueryClient();
  const [customerSlug, setCustomerSlug] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode | ''>('');
  const [auchNichtLinux, setAuchNichtLinux] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rolloutKind, setRolloutKind] = useState<RolloutKind | null>(null);
  const [entkoppelnOpen, setEntkoppelnOpen] = useState(false);
  const [grund, setGrund] = useState('');
  const [entkoppelnFehler, setEntkoppelnFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<string | null>(null);

  const filter: ZugangsmigrationFilter = {
    ...(customerSlug ? { customerSlug } : {}),
    ...(authMode ? { authMode } : {}),
    ...(auchNichtLinux ? { nurLinux: false } : {}),
  };
  const query = useZugangsmigration(filter);
  const preview = useRolloutPreview();
  const start = useStartRollout();
  const entkoppeln = useEntkoppelnZugang();

  const data = query.data;
  const fortschritt = data?.fortschritt;
  const systeme = data?.systeme ?? [];
  const rollout = data?.rollout;

  const customerOptions = useMemo(
    () =>
      (data?.kunden ?? []).map((k) => ({
        value: k.slug,
        label: k.name,
        hint: k.slug,
      })),
    [data?.kunden]
  );

  const selectedSysteme = systeme.filter((s) => selectedIds.has(s.system_id));

  /**
   * Filterwechsel leert die Auswahl.
   *
   * Sonst bleibt eine angehakte Zeile ausgewählt, die der neue Filter
   * ausblendet: der Bestätigungsdialog listet nur noch die sichtbaren
   * Hostnamen, entkoppelt aber auch den unsichtbaren. Ein Dialog, der nicht
   * alles nennt, worauf er wirkt, ist keine Bestätigung.
   */
  function filterWechsel(aendern: () => void): void {
    setSelectedIds(new Set());
    aendern();
  }

  function toggleSelected(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openRollout(kind: RolloutKind): void {
    if (!rollout) return;
    const vorschlag = kind === 'ausrollen' ? rollout.keys_ausrollen : rollout.key_pruefen;
    setErgebnis(null);
    preview.reset();
    start.reset();
    setRolloutKind(kind);
    preview.mutate({
      routineSlugs: [vorschlag.routine_slug],
      customerSlugs: vorschlag.customer_slugs,
      // Ohne die Ausschlussliste nimmt der Rollout JEDES passende System
      // dieser Kunden - auch die, die den Schlüssel schon haben, und die,
      // die jemand ausdrücklich auf 'manuell' gestellt hat.
      excludeSystemIds: vorschlag.exclude_system_ids,
    });
  }

  function startRollout(): void {
    if (!rollout || !rolloutKind) return;
    const vorschlag =
      rolloutKind === 'ausrollen' ? rollout.keys_ausrollen : rollout.key_pruefen;
    start.mutate(
      {
        routineSlugs: [vorschlag.routine_slug],
        customerSlugs: vorschlag.customer_slugs,
        // Muss zur Vorschau passen: startete der Lauf ohne diese Liste,
        // träfe er mehr Systeme als eben angezeigt.
        excludeSystemIds: vorschlag.exclude_system_ids,
        checkMode: rolloutKind === 'pruefen',
        snapshot: false,
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: (res) => {
          setRolloutKind(null);
          void qc.invalidateQueries({ queryKey: zugangsmigrationKeys.all });
          setErgebnis(
            `${res.totals.runs} Läufe bei ${res.totals.customers_with_runs} Kunden gestartet`
          );
        },
      }
    );
  }

  function submitEntkoppeln(): void {
    const trimmed = grund.trim();
    if (trimmed === '' || selectedIds.size === 0) return;
    setEntkoppelnFehler(null);
    entkoppeln.mutate(
      { system_ids: [...selectedIds], grund: trimmed },
      {
        onSuccess: (res) => {
          setSelectedIds(new Set());
          setEntkoppelnOpen(false);
          setGrund('');
          setErgebnis(
            res.entkoppelt === 1
              ? '1 Tresor-Verknüpfung entfernt.'
              : `${res.entkoppelt} Tresor-Verknüpfungen entfernt.`
          );
        },
        onError: (err) => {
          // 409: Dialog offen lassen, damit der Servertext zu den Systemen ohne Key steht.
          if (err instanceof ApiError && err.status === 409) {
            setEntkoppelnFehler(err.problem?.detail ?? err.message);
            return;
          }
          setEntkoppelnFehler(err instanceof Error ? err.message : 'Entkoppeln fehlgeschlagen.');
        },
      }
    );
  }

  const columns = useMemo<ReadonlyArray<Column<ZugangsmigrationSystem>>>(
    () => [
      {
        id: 'auswahl',
        header: <span className="sr-only">Auswahl</span>,
        filter: false,
        filterLabel: 'Auswahl',
        cell: (sys) => (
          <input
            type="checkbox"
            checked={selectedIds.has(sys.system_id)}
            onChange={() => toggleSelected(sys.system_id)}
            aria-label={`${sys.hostname} auswählen`}
            className="size-4 accent-primary"
          />
        ),
      },
      {
        id: 'kunde',
        header: 'Kunde',
        filterValue: (sys) => `${sys.customer_name} ${sys.customer_slug}`,
        cell: (sys) => (
          <>
            <div className="font-medium">{sys.customer_name}</div>
            <div className="font-mono text-xs text-muted">{sys.customer_slug}</div>
          </>
        ),
      },
      {
        id: 'host',
        header: 'Host',
        className: 'font-mono text-xs',
        cell: (sys) => sys.hostname,
      },
      {
        id: 'modus',
        header: 'Modus',
        filter: 'select',
        filterValue: (sys) => modusAnzeige(sys.auth_mode).text,
        cell: (sys) => {
          const modus = modusAnzeige(sys.auth_mode);
          return (
            <Badge tone={modus.tone} data-testid={`zugang-modus-${sys.system_id}`}>
              {modus.text}
            </Badge>
          );
        },
      },
      {
        id: 'tresor',
        header: 'Tresor',
        filter: 'select',
        filterValue: (sys) =>
          `${sys.passwort_im_tresor ? 'ja' : 'nein'}${sys.bw_item_kind === 'web' ? ' (Weblogin)' : ''}`,
        cell: (sys) => (
          <>
            {sys.passwort_im_tresor ? 'ja' : 'nein'}
            {sys.bw_item_kind === 'web' ? ' (Weblogin)' : ''}
          </>
        ),
      },
      {
        id: 'key-bestaetigt',
        header: 'Key bestätigt',
        filter: 'select',
        filterValue: (sys) => (sys.key_bestaetigt ? 'ja' : 'nein'),
        cell: (sys) =>
          sys.key_bestaetigt ? (
            <>
              <RelativeTime value={sys.key_verified_at} />
              {sys.key_verified_by ? ` (${sys.key_verified_by})` : null}
            </>
          ) : (
            'nein'
          ),
      },
      {
        id: 'letzter-zugang',
        header: 'Letzter Zugang',
        filterValue: (sys) => {
          const wegText =
            sys.letzter_zugang_weg === 'key'
              ? 'über Schlüssel'
              : sys.letzter_zugang_weg === 'passwort'
                ? 'über Passwort'
                : '';
          const okText =
            sys.letzter_zugang_ok === true
              ? 'erfolgreich'
              : sys.letzter_zugang_ok === false
                ? 'gescheitert'
                : 'noch nie gemeldet';
          return [wegText, okText].filter(Boolean).join(' ');
        },
        cell: (sys) => (
          <LetzterZugang weg={sys.letzter_zugang_weg} ok={sys.letzter_zugang_ok} />
        ),
      },
      {
        id: 'empfehlung',
        header: 'Empfehlung',
        filterValue: (sys) => [sys.empfehlung, sys.hinweis ?? ''].join(' ').trim(),
        cell: (sys) => (
          <span {...(sys.hinweis ? { title: sys.hinweis } : {})}>
            <div>{sys.empfehlung}</div>
            {sys.hinweis && <div className="text-xs text-muted">{sys.hinweis}</div>}
          </span>
        ),
      },
    ],
    [selectedIds]
  );

  const ausrollenN = rollout?.keys_ausrollen.anzahl_systeme ?? 0;
  const pruefenN = rollout?.key_pruefen.anzahl_systeme ?? 0;
  const auswahlN = selectedIds.size;
  const grundOk = grund.trim().length > 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h1 className="text-2xl font-semibold">Zugangsmigration</h1>
          <p className="mt-1 text-muted">
            Auf jedem Linux-System sollen zusätzlich die SSH-Schlüssel der drei Sysadmins
            liegen. Das Tresor-Passwort bleibt daneben bestehen und funktioniert weiter —
            der Schlüssel ist ein zweiter Weg, kein Ersatz. Der Zugang versucht zuerst den
            Schlüssel und fällt auf das Passwort zurück.
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className="size-4" />}
          onClick={() => {
            void qc.invalidateQueries({ queryKey: zugangsmigrationKeys.all });
          }}
          data-testid="zugang-reload"
        >
          Neu laden
        </Button>
      </header>

      {query.error && <ErrorAlert error={query.error} />}

      {fortschritt && (
        <Card title="Fortschritt">
          <div
            role="progressbar"
            aria-valuenow={fortschritt.prozent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Fortschritt der Zugangsmigration"
            className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
          >
            <div
              className="h-full bg-primary"
              style={{ width: `${Math.min(100, Math.max(0, fortschritt.prozent))}%` }}
            />
          </div>
          <p className="mt-2 text-sm" data-testid="zugang-fortschritt-text">
            <strong>
              {fortschritt.mit_key} von {fortschritt.zu_erledigen} Linux-Systemen
            </strong>{' '}
            haben den Schlüssel bestätigt ({fortschritt.prozent} %).
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
            <StatCard label="Nur Tresor-Passwort" value={fortschritt.passwort} />
            <StatCard label="Passwort + Key" value={fortschritt.passwort_und_key} />
            <StatCard label="Nur Key" value={fortschritt.key} />
            <StatCard
              label="Kein Weg bekannt"
              value={fortschritt.unbekannt}
              tone={fortschritt.unbekannt > 0 ? 'danger' : 'neutral'}
            />
          </div>
          <p className="mt-3 text-xs text-muted">
            {fortschritt.manuell} Systeme sind auf Handarbeit gestellt (OPNsense, Windows,
            Appliances) und zählen nicht als Rückstand.
          </p>
        </Card>
      )}

      <Card title="Filter">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <Combobox
              label="Kunde"
              value={customerSlug}
              onChange={(wert) => filterWechsel(() => setCustomerSlug(wert))}
              options={customerOptions}
              placeholder="Alle Kunden"
            />
          </div>
          <label className="flex min-w-[180px] flex-col gap-1 text-sm font-medium">
            Modus
            <select
              aria-label="Modus"
              data-testid="zugang-filter-modus"
              value={authMode}
              onChange={(e) => {
                const wert = e.target.value as AuthMode | '';
                filterWechsel(() => setAuthMode(wert));
              }}
              className="h-10 rounded-md border border-border bg-surface px-3 text-sm font-normal text-text"
            >
              {AUTH_MODE_OPTIONS.map((opt) => (
                <option key={opt.value || 'alle'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="mb-1 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={auchNichtLinux}
              onChange={(e) => {
                const an = e.target.checked;
                filterWechsel(() => setAuchNichtLinux(an));
              }}
              className="size-4 accent-primary"
              data-testid="zugang-filter-nur-linux"
            />
            auch Nicht-Linux-Systeme zeigen
          </label>
        </div>
      </Card>

      <Card title="Aktionen">
        {ergebnis && (
          <p
            className="mb-3 rounded-md border border-success/30 bg-success/5 p-2 text-sm"
            data-testid="zugang-ergebnis"
            role="status"
          >
            {ergebnis}
          </p>
        )}
        <div className="flex flex-col gap-4">
          <div>
            <Button
              data-testid="zugang-aktion-ausrollen"
              disabled={ausrollenN === 0}
              onClick={() => openRollout('ausrollen')}
            >
              Keys ausrollen (Vorschau) — {ausrollenN} Systeme
            </Button>
            <p className="mt-1 text-sm text-muted">
              Hinterlegt die öffentlichen SSH-Schlüssel der drei Sysadmins auf den
              Linux-Systemen, die sie noch nicht haben. Das Tresor-Passwort bleibt als
              zweiter Weg bestehen.
            </p>
          </div>
          <div>
            <Button
              variant="secondary"
              data-testid="zugang-aktion-pruefen"
              disabled={pruefenN === 0}
              onClick={() => openRollout('pruefen')}
            >
              Key-Zugang prüfen (Vorschau) — {pruefenN} Systeme
            </Button>
            <p className="mt-1 text-sm text-muted">
              Prüft, ob der Schlüsselzugang funktioniert, ohne etwas zu verändern.
            </p>
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="danger"
                data-testid="zugang-aktion-entkoppeln"
                disabled={auswahlN === 0}
                onClick={() => {
                  setEntkoppelnFehler(null);
                  setGrund('');
                  setEntkoppelnOpen(true);
                }}
              >
                Tresor-Verknüpfung entfernen ({auswahlN})
              </Button>
              <p className="max-w-xl text-xs text-muted">
                Nur wenn der Tresoreintrag selbst ungültig ist (falsches System, gedrehtes
                Passwort, gelöschtes Item). Im Regelfall bleibt das Passwort als Rückfall
                stehen.
              </p>
            </div>
          </div>
        </div>
      </Card>

      <Card title="Systeme">
        {query.isLoading && <SkeletonRows rows={5} cols={8} />}
        {!query.isLoading && systeme.length === 0 && !query.error && (
          <EmptyState
            icon={<KeyRound className="size-8" />}
            title="Keine Systeme"
            description="Unter den gewählten Filtern gibt es keine Systeme."
          />
        )}
        {systeme.length > 0 && (
          <DataTable
            caption="Zugangsmigration je System: Tresor-Passwort und Schlüssel nebeneinander"
            columns={columns}
            rows={systeme}
            rowKey={(sys) => sys.system_id}
            storageKey="zugangsmigration"
            rowSelected={(sys) => selectedIds.has(sys.system_id)}
            rowTestId={(sys) => `zugang-row-${sys.system_id}`}
          />
        )}
      </Card>

      <Dialog
        open={rolloutKind !== null}
        onClose={() => setRolloutKind(null)}
        title={rolloutKind === 'pruefen' ? 'Key-Zugang prüfen' : 'Keys ausrollen'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRolloutKind(null)}>
              Abbrechen
            </Button>
            <Button
              onClick={startRollout}
              loading={start.isPending}
              disabled={!preview.data || start.isPending}
            >
              Jetzt starten
            </Button>
          </>
        }
      >
        {preview.isPending && <p className="text-sm text-muted">Vorschau wird geladen …</p>}
        {preview.error && <ErrorAlert error={preview.error} />}
        {preview.data && <PreviewListe preview={preview.data} />}
        {rolloutKind === 'ausrollen' && (
          <div className="mt-3 flex flex-col gap-2 text-sm text-muted">
            <p>
              Ein Snapshot ist nicht vorgesehen: die Routine schreibt nur eine Zeile in
              authorized_keys, ein Abbild des Systems brächte dort nichts.
            </p>
            <p>
              Kein Trockenlauf: dabei würden keine Schlüssel hinterlegt. Dafür gibt es die
              Aktion „Key-Zugang prüfen“.
            </p>
          </div>
        )}
        {rolloutKind === 'pruefen' && (
          <p className="mt-3 text-sm text-muted">Liest nur nach, verändert nichts.</p>
        )}
        {start.error && (
          <div className="mt-3">
            <ErrorAlert error={start.error} />
          </div>
        )}
      </Dialog>

      <Dialog
        open={entkoppelnOpen}
        onClose={() => setEntkoppelnOpen(false)}
        title="Tresor-Verknüpfung entfernen"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEntkoppelnOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={submitEntkoppeln}
              disabled={!grundOk || entkoppeln.isPending}
              loading={entkoppeln.isPending}
              data-testid="zugang-entkoppeln-bestaetigen"
            >
              Verknüpfung entfernen
            </Button>
          </>
        }
      >
        <p className="text-sm">
          {auswahlN === 1
            ? '1 ausgewähltes System:'
            : `${auswahlN} ausgewählte Systeme:`}
        </p>
        <ul className="mt-2 list-disc pl-5 font-mono text-xs">
          {selectedSysteme.map((s) => (
            <li key={s.system_id}>{s.hostname}</li>
          ))}
        </ul>
        <label className="mt-4 flex flex-col gap-1">
          <span className="text-sm font-medium">Grund</span>
          <textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            aria-label="Grund"
            data-testid="zugang-entkoppeln-grund"
            rows={3}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
          />
        </label>
        {entkoppelnFehler && (
          <p role="alert" className="mt-3 text-sm text-danger">
            {entkoppelnFehler}
          </p>
        )}
      </Dialog>
    </div>
  );
}

function modusAnzeige(mode: AuthMode): { text: string; tone: BadgeTone } {
  switch (mode) {
    case 'passwort+key':
      return { text: 'Passwort + Key', tone: 'success' };
    case 'key':
      return { text: 'Nur Key', tone: 'success' };
    case 'passwort':
      return { text: 'Nur Passwort', tone: 'warning' };
    case 'unbekannt':
      return { text: 'Kein Weg bekannt', tone: 'danger' };
    case 'manuell':
      return { text: 'Handarbeit', tone: 'neutral' };
  }
}

function LetzterZugang({
  weg,
  ok,
}: {
  weg: ZugangsmigrationSystem['letzter_zugang_weg'];
  ok: boolean | null;
}): JSX.Element {
  const wegText = weg === 'key' ? 'über Schlüssel' : weg === 'passwort' ? 'über Passwort' : '—';
  return (
    <div className="flex flex-col gap-0.5">
      <span>{wegText}</span>
      <ZugangOk ok={ok} />
    </div>
  );
}

function ZugangOk({ ok }: { ok: boolean | null }): JSX.Element {
  // null darf nie wie ein Fehlschlag aussehen — es heißt nur "noch nie gemeldet".
  if (ok === true) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <Check className="size-3.5" aria-hidden="true" />
        erfolgreich
      </span>
    );
  }
  if (ok === false) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-danger">
        <X className="size-3.5" aria-hidden="true" />
        gescheitert
      </span>
    );
  }
  return <span className="text-xs text-muted">noch nie gemeldet</span>;
}

function PreviewListe({ preview }: { preview: RolloutPreviewResponse }): JSX.Element {
  const zeilen = preview.customers.flatMap((c) =>
    c.groups.map((g) => ({
      key: `${c.customer_id}-${g.routine_slug}`,
      text: `${c.name} — ${g.display_name} — ${g.hostnames.length} Hosts`,
    }))
  );
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm">
        {preview.totals.runs} {preview.totals.runs === 1 ? 'Lauf' : 'Läufe'} bei{' '}
        {preview.totals.customers_with_runs} Kunden, {preview.totals.systems} Systeme.
      </p>
      {zeilen.length === 0 ? (
        <p className="text-sm text-muted">Keine Systeme in der Vorschau.</p>
      ) : (
        <ul className="list-disc pl-5 text-sm">
          {zeilen.map((z) => (
            <li key={z.key}>{z.text}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
